export type JourneyLeaveChoice = "save" | "discard" | "stay";
/** 只能丢弃、没有地方可存的离开内容（如还没建草稿时已传的文件）：确认框只给「丢弃 / 留下」。 */
export type JourneyDiscardPrompt = {
  /** 确认框标题，也是对话框的可访问名，如「丢掉已传的 2 个文件并退出？」 */
  title: string;
  /** 丢弃按钮的文字，如「丢掉文件并退出」 */
  confirm: string;
  /** 一句说明留下会怎样 */
  hint: string;
  /** 列表上面那句「离开后什么不会保留」；不给就是 W6 原句「还没建报价草稿，离开后这些不会保留：」 */
  description?: string;
};
export type JourneyEditor = {
  label: string;
  dirty: () => boolean;
  save: () => Promise<boolean>;
  discard: () => void | Promise<void>;
  /** 有它且脏时，本次确认用只能丢弃的写法（W6）。 */
  discardOnly?: JourneyDiscardPrompt;
  /** 本次离开会把原页暂存/停放（去报价设置）时不算会丢的修改。 */
  keptAcrossSettings?: boolean;
};
export type JourneyLeaveOptions = {
  currentKey: () => string;
  problem: (target?: string) => string | null;
  /** keepsWork：本次离开会把原页暂存或停放，登记表可据此不计 keptAcrossSettings 的项。 */
  editors: (ids?: readonly string[], keepsWork?: boolean) => JourneyEditor[];
  ask: (
    labels: string[],
    discardOnly?: JourneyDiscardPrompt,
  ) => Promise<JourneyLeaveChoice>;
  notice: (message: string | null) => void;
  finished?: () => void;
};
/** A deferred decision performs no save/discard until its caller commits all owners. */
export type JourneyLeavePlan = {
  commitKind: "save" | "discard";
  commit: () => Promise<boolean>;
  cancel: () => void;
};
export function createJourneyLeave(options: JourneyLeaveOptions) {
  const stopped = Symbol("disposed");
  let closed = false,
    running = false;
  let stop!: (v: typeof stopped) => void;
  const canceled = new Promise<typeof stopped>((resolve) => {
    stop = resolve;
  });
  function run(
    target: string | undefined,
    ids: readonly string[] | undefined,
    keepsWork: boolean,
    deferred: true,
  ): Promise<JourneyLeavePlan | null>;
  function run(
    target: string | undefined,
    ids: readonly string[] | undefined,
    keepsWork: boolean,
    deferred: false,
  ): Promise<boolean>;
  async function run(
    target: string | undefined,
    ids: readonly string[] | undefined,
    keepsWork: boolean,
    deferred: boolean,
  ): Promise<JourneyLeavePlan | boolean | null> {
    if (closed || running) return deferred ? null : false;
    const key = options.currentKey();
    const current = () => !closed && options.currentKey() === key;
    const problem = () => {
      const reason = options.problem(target);
      if (reason) options.notice(reason);
      return Boolean(reason);
    };
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      running = false;
      options.finished?.();
    };
    running = true;
    try {
      options.notice(null);
      if (problem()) {
        finish();
        return deferred ? null : false;
      }
      const dirty = options
        .editors(ids, keepsWork)
        .filter((editor) => editor.dirty());
      const discardOnly = dirty.find(
        (editor) => editor.discardOnly,
      )?.discardOnly;
      const choice = dirty.length
        ? await Promise.race([
            discardOnly
              ? options.ask(
                  dirty.map((e) => e.label),
                  discardOnly,
                )
              : options.ask(dirty.map((e) => e.label)),
            canceled,
          ])
        : "save";
      if (
        !current() ||
        choice === stopped ||
        choice === "stay" ||
        (discardOnly && choice === "save") ||
        problem()
      ) {
        finish();
        return deferred ? null : false;
      }
      // Only deferred project coordination closes the question before asking another owner.
      if (deferred) options.finished?.();
      const plan: JourneyLeavePlan = {
        commitKind: choice,
        cancel: finish,
        commit: async () => {
          if (finished) return false;
          try {
            if (!current() || problem()) return false;
            for (const editor of dirty) {
              const result = await Promise.race([
                choice === "save"
                  ? editor.save()
                  : Promise.resolve(editor.discard()).then(() => true),
                canceled,
              ]);
              if (!current() || result === stopped) return false;
              if (!result) {
                options.notice(
                  `${editor.label}没有保存成功，请检查填写内容后重试`,
                );
                return false;
              }
              if (problem()) return false;
            }
            return true;
          } catch {
            if (current())
              options.notice("修改尚未处理完成，请留在这里检查后重试");
            return false;
          } finally {
            finish();
          }
        },
      };
      // Preserve the original same-continuation discard/field-capture ordering.
      return deferred ? plan : plan.commit();
    } catch {
      if (current()) options.notice("修改尚未处理完成，请留在这里检查后重试");
      finish();
      return deferred ? null : false;
    }
  }
  return {
    prepare(target?: string, ids?: readonly string[], keepsWork = false) {
      return run(target, ids, keepsWork, true);
    },
    request(
      target?: string,
      ids?: readonly string[],
      keepsWork = false,
    ): Promise<boolean> {
      return run(target, ids, keepsWork, false);
    },
    dispose() {
      closed = true;
      stop(stopped);
    },
  };
}
